import { animate, style, transition, trigger } from '@angular/animations';
import {
  Component, Input, OnChanges, SimpleChanges, ViewChild, inject
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SharedService } from '@shared/services/shared.service';
import { IHospitalServiceLocation } from 'app/models/licenses/hospital/hospital-service-location';
import { HospitalServiceLocationService } from 'app/services/license/hospital/hospital-service-location.service';

@Component({
  selector: 'app-hospital-service-location',
  templateUrl: './hospital-service-location.component.html',
  styleUrl: './hospital-service-location.component.scss',
  animations: [
    trigger('slideRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ])
  ]
})

export class HospitalServiceLocationComponent implements OnChanges {
  @Input() entityId = 0;
  @Input() entityType = '';
  @Input() showType = true;
  @Input() canEdit = false;

  @ViewChild(MatPaginator) set paginator(paginator: MatPaginator | undefined) {
    this.dataSource.paginator = paginator ?? null;
  }

  @ViewChild(MatSort) set sort(sort: MatSort | undefined) {
    this.dataSource.sort = sort ?? null;
  }

  public readonly dataSource = new MatTableDataSource<IHospitalServiceLocation>([]);
  public readonly locationForm = new FormGroup({
    floor: new FormControl('', { nonNullable: true, validators: Validators.required }),
    suite: new FormControl('', { nonNullable: true, validators: Validators.required }),
    servicesProvided: new FormControl('', { nonNullable: true, validators: Validators.required }),
    typeOutpatient: new FormControl(false),
    typePsychiatric: new FormControl(false),
    typeEmergency: new FormControl(false)
  }, { validators: this.atLeastOneTypeValidator() });

  public entity: string = 'HospitalServiceLocation';
  public includeArchived = false;
  public isLoading = false;
  public isSaving = false;
  public managingLocation = false;
  public location: IHospitalServiceLocation = this.getEmptyLocation();

  private readonly locationService = inject(HospitalServiceLocationService);
  private readonly sharedService = inject(SharedService);

  public get displayedColumns(): string[] {
    const columns = ['floor', 'suite'];
    if (this.showType) {
      columns.push('type');
    }
    return [...columns, 'servicesProvided', 'archived'];
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.entityId || changes.entityType) {
      this.closeEditor();
      this.loadLocations(this.includeArchived);
    }
  }

  public loadLocations(includeArchived: boolean): void {
    this.includeArchived = includeArchived;

    if (!this.entityId || !this.entityType) {
      this.isLoading = false;
      this.dataSource.data = [];
      return;
    }

    this.isLoading = true;
    this.locationService.getHospitalServiceLocations(
      this.entityId,
      this.entityType,
      this.includeArchived
    ).subscribe({
      next: locations => {
        this.dataSource.data = locations;
      },
      error: error => {
        this.isLoading = false;
        this.sharedService.toastr.error('Could not load hospital service locations');
        console.error('Error loading hospital service locations', error);
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  public addLocation(): void {
    this.openEditor(this.getEmptyLocation());
  }

  public editLocation(location: IHospitalServiceLocation): void {
    this.openEditor({ ...location });
  }

  public closeEditor(): void {
    this.managingLocation = false;
    this.isSaving = false;
    this.location = this.getEmptyLocation();
    this.locationForm.reset();
  }

  parseTypes(typeString: string): string[] {
    if (!typeString) {
      return [];
    }
    try {
      const parsed = JSON.parse(typeString);
      return Array.isArray(parsed) ? parsed : [typeString];
    } catch {
      return [typeString];
    }
  }

  formatTypeForDisplay(typeString: string): string {
    const types = this.parseTypes(typeString);
    return types.join(', ');
  }

  getSelectedTypesAsJson(): string {
    const types: string[] = [];

    if (this.locationForm.get('typeOutpatient')?.value) {
      types.push('Outpatient Satellite');
    }
    if (this.locationForm.get('typePsychiatric')?.value) {
      types.push('Psychiatric Satellite');
    }
    if (this.locationForm.get('typeEmergency')?.value) {
      types.push('Emergency Medical Services Satellite');
    }

    return types.length > 0 ? JSON.stringify(types) : '';
  }

  public saveLocation(): void {
    if (this.locationForm.invalid || this.isSaving) {
      this.locationForm.markAllAsTouched();
      return;
    }

    const form = this.locationForm.getRawValue();
    const location: IHospitalServiceLocation = {
      ...this.location,
      floor: form.floor.trim(),
      suite: form.suite.trim(),
      type: this.showType ? this.getSelectedTypesAsJson() : this.location.type,
      servicesProvided: form.servicesProvided.trim()
    };

    this.isSaving = true;
    if (location.id === 0) {
      this.locationService.createHospitalServiceLocation(location, this.entityId, this.entityType).subscribe({
        next: createdLocation => {
          this.dataSource.data = [...this.dataSource.data, createdLocation];
          this.location.id = createdLocation.id;
          this.sharedService.toastr.success('Hospital service location added');
        },
        error: error => this.handleSaveError('Could not add hospital service location', error),
        complete: () => {
          setTimeout(() => {
            this.closeEditor();
          }, 500);
        }
      });
      return;
    }

    this.locationService.updateHospitalServiceLocation(location).subscribe({
      next: updated => {
        if (!updated) {
          this.handleSaveError('Could not update hospital service location');
          return;
        }

        this.dataSource.data = this.dataSource.data.map(existing =>
          existing.id === location.id ? location : existing
        );
        this.sharedService.toastr.success('Hospital service location updated');
        this.closeEditor();
      },
      error: error => this.handleSaveError('Could not update hospital service location', error)
    });
  }

  public archiveLocation(location: IHospitalServiceLocation, archive: boolean): void {
    const action = archive ? 'Archive' : 'Un-archive';
    this.sharedService.openConfirm(`${action} this hospital service location?`);
    this.sharedService.confirmed().subscribe(confirmed => {
      if (!confirmed) {
        return;
      }

      this.isSaving = true;
      this.locationService.archiveHospitalServiceLocation(location.id, archive).subscribe({
        next: archived => {
          if (!archived) {
            this.isSaving = false;
            this.sharedService.toastr.error(`Could not ${action.toLowerCase()} hospital service location`);
            return;
          }

          this.sharedService.toastr.success(`Hospital service location ${archive ? 'archived' : 'restored'}`);
          this.closeEditor();
          this.loadLocations(this.includeArchived);
        },
        error: error => {
          this.isSaving = false;
          this.sharedService.toastr.error(`Could not ${action.toLowerCase()} hospital service location`);
          console.error('Error changing hospital service location archive status', error);
        }
      });
    });
  }

  public deleteLocation(location: IHospitalServiceLocation): void {
    this.sharedService.openConfirm('Permanently delete this hospital service location?');
    this.sharedService.confirmed().subscribe(confirmed => {
      if (!confirmed) {
        return;
      }

      this.isSaving = true;
      this.locationService.deleteHospitalServiceLocation(location.id).subscribe({
        next: deleted => {
          if (!deleted) {
            this.isSaving = false;
            this.sharedService.toastr.error('Could not delete hospital service location');
            return;
          }

          this.dataSource.data = this.dataSource.data.filter(existing => existing.id !== location.id);
          this.sharedService.toastr.success('Hospital service location deleted');
          this.closeEditor();
        },
        error: error => {
          this.isSaving = false;
          this.sharedService.toastr.error('Could not delete hospital service location');
          console.error('Error deleting hospital service location', error);
        }
      });
    });
  }

  private openEditor(location: IHospitalServiceLocation): void {
    this.location = location;
    const types = this.parseTypes(location.type);

    this.locationForm.reset({
      floor: location.floor,
      suite: location.suite,
      typeOutpatient: types.includes('Outpatient Satellite'),
      typePsychiatric: types.includes('Psychiatric Satellite'),
      typeEmergency: types.includes('Emergency Medical Services Satellite'),
      servicesProvided: location.servicesProvided
    });
    this.managingLocation = true;
  }

  private getEmptyLocation(): IHospitalServiceLocation {
    return {
      id: 0,
      floor: '',
      suite: '',
      servicesProvided: '',
      type: '',
      archived: false
    };
  }

  private handleSaveError(message: string, error?: unknown): void {
    this.isSaving = false;
    this.sharedService.toastr.error(message);
    if (error) {
      console.error(message, error);
    }
  }

  private atLeastOneTypeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if(!this.showType) return null;
      const formGroup = control as FormGroup;
      const hasAtLeastOne = formGroup?.get('typeOutpatient')?.value ||
                            formGroup?.get('typePsychiatric')?.value ||
                            formGroup?.get('typeEmergency')?.value;
      return hasAtLeastOne ? null : { noTypeSelected: true };
    };
  }
}
